// page-backlog-review-constat.ts — Le haut de l'écran de revue : ce qu'on sait.
//
// Extrait de `page-backlog-review.ts` le 2026-09-13. Quatre blocs, tous en
// LECTURE, rendus à partir de la revue et d'un état explicite (replié ou non) :
// la passe anormale, le cadrage, la dérive depuis l'analyse, le constat figé.
// Rien ici ne décide ni n'écrit — le seul geste, enregistrer le titre et le
// plafond, est câblé par l'écran, qui possède le rechargement.
//
// Globaux ambiants : esc, t, openModal, closeModal (core-globals.d.ts).

import { fmtDay } from './page-backlog-reviews.js';
import { label, type Review } from './page-backlog-review-types.js';

// ── Constat ──────────────────────────────────────────────────────────────────

/** Un compteur du constat. `help` porte une infobulle, jamais du contenu.
 *
 * Séparé du titre parce que le titre est ÉCHAPPÉ : y glisser du markup le
 * ferait afficher tel quel. Le paramètre est construit ici, à partir d'un
 * libellé traduit et échappé — jamais d'une saisie.
 */
function _counterBlock(title: string, lines: string[], helpId = ''): string {
  const tip = helpId
    ? ` <button type="button" id="${helpId}" class="help-tip" aria-label="${esc(t('review.constat.vitalityHelp'))}" title="${esc(t('review.constat.vitalityHelp'))}">?</button>`
    : '';
  return `<div><div class="text-xs text-muted" style="text-transform:uppercase;letter-spacing:.04em;margin-bottom:6px">${esc(title)}${tip}</div>${lines.join('')}</div>`;
}

const VITALITY_ORDER = ['alive', 'churning', 'fleeing', 'social', 'dormant'] as const;

/** Le barème des cinq profils, dans une modale.
 *
 * Pas une infobulle `title` : cet attribut ne se met pas en forme, et cinq
 * définitions d'affilée sans libellé détaché y étaient illisibles. Ici chaque
 * profil est en gras, sur sa ligne.
 */
export function openVitalityHelp(): void {
  const rows = VITALITY_ORDER.map(
    (code) =>
      `<li style="margin:6px 0"><strong>${esc(label('review.vitality', code))}</strong> — ${esc(
        label('review.vitalityDef', code),
      )}</li>`,
  ).join('');
  openModal(
    t('review.constat.vitalityHelp'),
    `<ul style="margin:0;padding-left:18px;list-style:none">${rows}</ul>`,
    () => closeModal(),
    t('common.close'),
  );
  const cancel = document.getElementById('modal-cancel');
  if (cancel) cancel.style.display = 'none';
}

// Les pastilles portent TOUJOURS leur libellé : aucune information ne doit
// tenir à la seule couleur. La table est une constante de module : c'est ce
// qui prouve au cliquet `check_style_attr.py` que la couleur ne vient pas
// d'une donnée.
const VITALITY_TONES: Record<string, string> = {
  alive: 'var(--ok)',
  churning: 'var(--warn)',
  fleeing: 'var(--danger)',
  social: 'var(--warn)',
  dormant: 'var(--danger)',
};

function _vitalityLines(vitality: Record<string, number>): string[] {
  return Object.keys(VITALITY_TONES).map(
    (code) =>
      `<div style="display:flex;align-items:center;gap:8px;margin-bottom:2px">
        <span style="width:8px;height:8px;border-radius:50%;background:${VITALITY_TONES[code]}"></span>
        <strong>${vitality?.[code] ?? 0}</strong>
        <span class="text-muted">${esc(label('review.vitality', code))}</span>
      </div>`,
  );
}

export function renderConstat(review: Review, collapsed: boolean): string {
  const c = review.constat || {};
  const volumes = (c.volumes || {}) as Record<string, number>;
  const dup = (c.duplicates || {}) as Record<string, number | string>;
  const ref = (c.referential || {}) as Record<string, number>;
  const hidden = volumes.hidden_actions || 0;
  // Le bloc « Priorisation » a été RETIRÉ le 2026-08-26. Il comptait le
  // périmètre, les cartes juste en dessous montrent les recommandations, et
  // rien ne disait que ce sont deux populations différentes : on lisait « 12 en
  // priorité haute » au-dessus de 5 cartes et on cherchait l'erreur. Le compte
  // était juste, l'écran trompeur. Ce qu'il apportait — savoir si la priorité
  // discrimine encore — ne valait pas cette confusion permanente.
  const blocks = [
    _counterBlock(
      t('review.constat.vitality'),
      _vitalityLines((c.vitality || {}) as never),
      'btn-vitality-help',
    ),
    _counterBlock(t('review.constat.duplicates'), [
      `<div><strong>${dup.retained ?? 0}</strong> ${esc(t('review.constat.candidates'))}</div>`,
      `<div class="text-xs text-muted">${esc(label('review.method', String(dup.method || 'none')))}</div>`,
    ]),
    _counterBlock(t('review.constat.referential'), [
      `<div><strong>${ref.checklist_gaps ?? 0}</strong> ${esc(t('review.constat.checklistGaps'))}</div>`,
      `<div><strong>${ref.uncovered_risks ?? 0}</strong> ${esc(t('review.constat.uncoveredRisks'))}</div>`,
    ]),
  ];
  // Plier / déplier au chevron, comme le volet de filtres de `#actions` : c'est
  // le titre lui-même qui commande, pas un bouton à côté. Même markup, même
  // rotation, même `aria-expanded`.
  return `<div class="card" id="constat-card">
      <div class="card-header">
        <button id="btn-collapse" type="button" aria-expanded="${!collapsed}"
          aria-controls="constat-body"
          title="${esc(collapsed ? t('review.constat.expand') : t('review.constat.collapse'))}"
          style="display:flex;align-items:center;gap:6px;background:none;border:none;cursor:pointer;padding:0;font:inherit;text-align:left">
          <span style="font-size:11px;display:inline-block;transition:transform .15s;transform:rotate(${collapsed ? '-90deg' : '0deg'})">v</span>
          <span class="card-title">${esc(t('review.constat.title'))}
            <span class="text-muted text-xs"> — ${esc(t('review.constat.frozenAt', { date: fmtDay(review.constat_computed_at) }))}</span>
          </span>
        </button>
      </div>
      <div id="constat-body" style="${collapsed ? 'display:none' : ''}">
        <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:20px;margin-bottom:12px">${blocks.join('')}</div>
        <div class="toast-info" style="position:static;padding:10px">
          ⓘ ${esc(String(c.limits_notice || ''))}
          ${hidden ? ' ' + esc(t('review.constat.hidden', { n: String(hidden) })) : ''}
        </div>
      </div>
    </div>`;
}

// ── Cadrage ──────────────────────────────────────────────────────────────────

/** L'entête de cadrage : ce que la revue A ÉTÉ CRÉÉE pour couvrir.
 *
 * Tout y est en LECTURE, sauf le plafond d'affichage. Titre et périmètre
 * identifient la revue : les rouvrir rendrait le constat incohérent avec ce
 * qu'il annonce porter. Le plafond ne masque que l'affichage, il ne perd rien.
 *
 * Replié par défaut : on revient sur une revue pour arbitrer, pas pour relire
 * son cadrage. Même chevron que le volet de filtres de `#actions` et que le
 * constat.
 */
export function renderParams(review: Review, collapsed: boolean): string {
  const lines = [
    [t('review.params.scope'), review.label],
    [t('review.params.openedAt'), fmtDay(review.created_at)],
    [t('review.params.by'), review.created_by_trigram || '—'],
    [t('review.params.iterations'), String(review.iterations_count || 0)],
  ]
    .map(
      ([name, value]) =>
        `<div><span class="text-muted text-xs">${esc(String(name))}</span><br>${esc(String(value))}</div>`,
    )
    .join('');
  return `<div class="card" id="params-card">
      <div class="card-header">
        <button id="btn-params-collapse" type="button" aria-expanded="${!collapsed}"
          aria-controls="params-body"
          title="${esc(collapsed ? t('review.params.expand') : t('review.params.collapse'))}"
          style="display:flex;align-items:center;gap:6px;background:none;border:none;cursor:pointer;padding:0;font:inherit;text-align:left">
          <span style="font-size:11px;display:inline-block;transition:transform .15s;transform:rotate(${collapsed ? '-90deg' : '0deg'})">v</span>
          <span class="card-title">${esc(t('review.params.legend'))}</span>
        </button>
      </div>
      <div id="params-body" style="${collapsed ? 'display:none' : ''}">
        <div class="form-group">
          <label for="param-title">${esc(t('review.params.title'))}</label>
          <input type="text" id="param-title" maxlength="200" value="${esc(review.title || '')}"
            placeholder="${esc(t('review.new.titlePlaceholder'))}">
        </div>
        <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:16px;margin-bottom:10px">${lines}</div>
        <label style="display:flex;align-items:center;gap:8px">
          <input type="checkbox" id="param-limit"${review.limit_recommendations ? ' checked' : ''}>
          ${esc(t('review.new.limit'))}
        </label>
        <div style="margin-top:12px">
          <button type="button" class="btn btn-primary btn-sm" id="btn-params-save">
            ${esc(t('common.save'))}
          </button>
        </div>
      </div>
    </div>`;
}

// ── Dérive ───────────────────────────────────────────────────────────────────

/** Le libellé accordé au nombre. En français, 0 ET 1 prennent le singulier.
 *
 * La clé au singulier porte le suffixe `One`. Une convention plutôt qu'une
 * table : ajouter un compteur ne doit pas obliger à toucher un registre.
 */
export function plural(n: number, key: string, params?: Record<string, string>): string {
  return t(n < 2 ? `${key}One` : key, params);
}

export function renderDrift(review: Review): string {
  const d = review.drift || { analysed: 0, created_unanalysed: 0, left_perimeter: 0 };
  // Le troisième nombre est le plus important : une action créée depuis
  // l'analyse n'est citée par aucune recommandation, donc rien d'autre ne la
  // signalerait.
  return `<div class="card" style="padding:10px;border-left:3px solid var(--accent)">
      ⓘ ${esc(plural(d.analysed, 'review.drift.analysed', { n: String(d.analysed) }))} ·
      <strong>${d.created_unanalysed}</strong> ${esc(plural(d.created_unanalysed, 'review.drift.created'))} ·
      <strong>${d.left_perimeter}</strong> ${esc(plural(d.left_perimeter, 'review.drift.left'))}
    </div>`;
}

// ── Passe anormale ───────────────────────────────────────────────────────────

const TARGET_PREFIXES: Record<string, string> = { action: 'A-', risk: 'R-', phase: 'PH-' };

/** « action:12 » → « A-12 » ; une cible inconnue est rendue telle quelle. */
export function targetRef(raw: string): string {
  const [type, id] = raw.split(':');
  const prefix = TARGET_PREFIXES[type || ''];
  return prefix ? `${prefix}${id}` : raw;
}

/** Ce qui a l'air inhabituel dans ce qu'une source non fiable a produit.
 *
 * **Une alerte, jamais un blocage.** Les propositions restent affichées et
 * arbitrables : un référentiel réellement à l'abandon existe, et bloquer sur un
 * seuil ferait perdre cette passe-là sans moyen de passer outre.
 *
 * Le bloc dit ce qu'on a mesuré, avec ses nombres, et les trois causes
 * possibles — dont l'injection, sans la nommer comme la seule. Un signal qui
 * conclurait à la malveillance mentirait : la même forme sort d'un modèle qui a
 * mal compris le périmètre.
 */
export function renderAnomalies(review: Review): string {
  const signals = review.anomalies || [];
  if (!signals.length) return '';
  const lines = signals.map((s) => {
    const text = t(`review.anomaly.${s.code}`, {
      count: String(s.count),
      total: String(s.total),
      pct: String(s.pct),
      target: targetRef(s.target || ''),
    });
    return `<li>${esc(text)}</li>`;
  });
  return `<div class="card" role="status" style="padding:12px;margin-bottom:8px;border-left:3px solid var(--warn)">
      <strong>⚠ ${esc(t('review.anomaly.title'))}</strong>
      <div class="text-xs text-muted" style="margin-top:2px">${esc(t('review.anomaly.intro'))}</div>
      <ul style="margin:8px 0 0 18px">${lines.join('')}</ul>
      <div class="text-xs text-muted" style="margin-top:8px">${esc(t('review.anomaly.why'))}</div>
    </div>`;
}
